import { useId } from 'react';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, type AppMutationResult } from '@/api/mutation';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { currentFeedbackSource } from '@/api/feedbackSourceScope';

type BaseMutationOptions<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'onError'
> & {
  successMessage?: string;
  /** One binding, many rows or actions: see `useMutation` (#1628-d4). */
  feedbackKeyOf?: (variables: TVariables) => string | number;
};

type MutationErrorHandler<TData, TError, TVariables, TContext> = NonNullable<
  UseMutationOptions<TData, TError, TVariables, TContext>['onError']
>;

/**
 * Either the hook reports failures (optionally with an `errorMessage`
 * fallback), or the caller supplies `onError` and reports them itself.
 * Never both: a caller's `onError` REPLACES the default push, so an
 * `errorMessage` beside it is dead config (#878 — it hid a swallowed 400).
 */
type UseApiMutationOptions<TData, TError, TVariables, TContext> =
  | (BaseMutationOptions<TData, TError, TVariables, TContext> & {
      onError?: undefined;
      /** Fallback when the error carries no server message. */
      errorMessage?: string;
    })
  | (BaseMutationOptions<TData, TError, TVariables, TContext> & {
      onError: MutationErrorHandler<TData, TError, TVariables, TContext>;
      /** Not allowed with `onError`: the caller's handler reports instead. */
      errorMessage?: never;
    });

export function useApiMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseApiMutationOptions<TData, TError, TVariables, TContext>,
): AppMutationResult<TData, TError, TVariables, TContext> {
  const zone = useFeedbackZone();
  // One hook instance is one action (#1628): the 5xx central routing and this
  // hook's own 4xx report share the key, and the wrapper clears it on success.
  const key = useId();
  const {
    onError: userOnError,
    onSuccess: userOnSuccess,
    errorMessage = 'Something went wrong',
    successMessage,
    ...rest
  } = options;

  return useMutation({
    ...rest,
    feedbackSource: key,
    // The caller's onSuccess / onError result is RETURNED so TanStack awaits
    // it, exactly as raw useMutation does (#869): mutateAsync settles only
    // after the follow-up does. A rejected onSuccess follow-up fails the
    // mutation (MutationCache onError, then onError below, then mutateAsync
    // rejects), so a caller whose follow-up is non-fatal (a refresh after a
    // successful write) catches it inside its own callback. Guard:
    // test_869_guard_use_api_mutation_callback_settles in
    // awaitedCallbackRejectionHandled.guard.test.ts.
    onSuccess: (data, variables, onMutateResult, context) => {
      if (successMessage) {
        zone?.push(successMessage, 'success', { source: key });
      }
      return userOnSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (userOnError) {
        return userOnError(error, variables, onMutateResult, context);
      }
      // 5xx and transport failures are surfaced centrally by routeMutationError
      // (the QueryClient cache callback), in this dialog's band when the hook
      // runs inside one (#1174) — pushing here too would show it twice. Only
      // 4xx / validation / non-HTTP errors are this hook's.
      reportMutationError(zone, error, errorMessage, currentFeedbackSource() ?? key);
    },
  });
}
